import Vue from 'vue'
import Vuex from 'vuex'
import { randomId, deepMerge, cleanTree } from '@/utils'
import cloneDeep from 'lodash.clonedeep'
import componentLookup from '@/components'
import rootStore from '@/store/root'

Vue.use(Vuex)

export default (plugins) => {
  const mutations = {
    setProp (state, { key, value, locale }) {
      if (locale) {
        if (!state._props_locales) {
          Vue.set(state, '_props_locales', {})
        }
        if (!state._props_locales[locale]) {
          Vue.set(state._props_locales, locale, {})
        }
        if (value === undefined) {
          Vue.delete(state._props_locales[locale], key)
        } else {
          Vue.set(state._props_locales[locale], key, value)
        }
      } else {
        if (value === undefined) {
          Vue.delete(state._props, key)
        } else {
          Vue.set(state._props, key, value)
        }
      }
    },
    addChild (state, { name, child }) {
      if (!name) {
        throw new Error('Can\'t add child without name.')
      }
      if (name && state._children.indexOf(name) > -1) {
        throw new Error('Can\'t add child. Name duplicate.')
      }
      if (!componentLookup[child._type]) {
        throw new Error('Can\'t add child. Unknown type: ' + child._type)
      }
      Vue.set(state, name, child)
      state._children.push(name)
    },
    removeChild (state, { name }) {
      const position = state._children.indexOf(name)
      if (position > -1) {
        Vue.delete(state._children, position)
      }
      Vue.delete(state, name)
    },
    moveChild (state, payload) {
      const { name, movement } = payload
      const oldPosition = state._children.indexOf(name)
      if (oldPosition > -1) {
        Vue.delete(state._children, oldPosition)
        const newPosition = Math.max(0, oldPosition + movement)
        switch (movement) {
          case Infinity:
            state._children.push(name)
            break
          case -Infinity:
            state._children.unshift(name)
            break
          default:
            Vue.set(state, '_children', [
              ...state._children.slice(0, newPosition),
              name,
              ...state._children.slice(newPosition)
            ])
        }
        payload._children = state._children
      }
    }
  }

  const moduleLookup = Vue.observable({})

  const actions = {
    async registerModule (parent, { name }) {
      if (moduleLookup[parent.getters.path]) {
        // TODO: might cause bugs
      } else {
        // store parent in lookup array
        Vue.set(moduleLookup, parent.getters.path, parent)
      }
      // register new Module if name provided
      if (name) {
        // register if not registered yet
        if (!parent.getters.childrenByName[name]) {
          const newPath = [...parent.getters._path, name]
          const type = parent.state[name]._type
          const module = createModule(newPath, type)
          store.registerModule(newPath, module, { preserveState: true })
        }
        // register children as well
        if (parent.state[name] && Array.isArray(parent.state[name]._children) && parent.state[name]._children.length > 0) {
          for (const child of parent.state[name]._children) {
            await store.dispatch([...parent.getters._path, name, 'registerModule'].join('/'), { name: child })
          }
        } else {
          // just call for adding the lookup
          await store.dispatch([...parent.getters._path, name, 'registerModule'].join('/'), {})
        }
      } else if (Array.isArray(parent.state._children) && parent.state._children.length > 0) {
        // allow initialization without name but still recurse to children
        for (const child of parent.state._children) {
          await store.dispatch([...parent.getters._path, 'registerModule'].join('/'), { name: child })
        }
      }
    },
    unregisterModule (module) {
      Object.keys(moduleLookup)
        .filter(p => p.startsWith(module.getters.path))
        .forEach(p => Vue.delete(moduleLookup, p))
      store.unregisterModule(module.getters._path)
    },
    setProp ({ commit }, { key, value }) {
      commit('setProp', { key, value })
    },
    async setChild ({ state, dispatch }, payload) {
      for (const oldChild of state._children) {
        if (!payload.slot || payload.slot === state[oldChild]._slot) {
          await dispatch('removeChild', { name: oldChild })
        }
      }
      if (payload.childType !== undefined) {
        return dispatch('addChild', payload)
      }
    },
    async addChild ({ dispatch, commit, state, getters }, { childType, slot, name, props = {} }) {
      if (rootStore.getters.localeMode && !getters.path.startsWith('/assets')) {
        throw new Error('Can\'t add child. Locale mode active.')
      }
      name = name === undefined ? randomId(state._children) : name
      commit('addChild', {
        name,
        child: {
          _type: childType,
          _slot: slot,
          _props: { ...props },
          _children: []
        }
      })
      await dispatch('registerModule', { name })
      return getters.childrenByName[name]
    },
    remove ({ dispatch, getters }) {
      return dispatch([...getters._path.slice(0, -1), 'removeChild'].join('/'), { name: getters.name }, { root: true })
    },
    removeChild ({ dispatch, commit, getters }, { name }) {
      if (rootStore.getters.localeMode && !getters.path.startsWith('/assets')) {
        throw new Error('Can\'t remove child. Locale mode active.')
      }
      commit('removeChild', { name })
      return dispatch([name, 'unregisterModule'].join('/'))
    },
    move ({ dispatch, getters }, { movement }) {
      return dispatch([...getters._path.slice(0, -1), 'moveChild'].join('/'), { name: getters.name, movement }, { root: true })
    },
    moveChild ({ commit, getters }, { name, movement }) {
      if (rootStore.getters.localeMode && !getters.path.startsWith('/assets')) {
        throw new Error('Can\'t move child. Locale mode active.')
      }
      commit('moveChild', {
        name,
        movement
      })
      return getters.childrenByName[name]
    },
    clone ({ dispatch, getters }, { props }) {
      return dispatch([...getters._path.slice(0, -1), 'cloneChild'].join('/'), { name: getters.name, props }, { root: true })
    },
    async cloneChild ({ dispatch, commit, state, getters }, { name, props = {} }) {
      if (rootStore.getters.localeMode && !getters.path.startsWith('/assets')) {
        throw new Error('Can\'t clone child. Locale mode active.')
      }
      const newName = randomId(state._children)
      const child = cloneDeep(state[name])
      child._props = {
        ...child._props,
        ...props
      }
      commit('addChild', { name: newName, child })
      await dispatch('registerModule', { name: newName })
      return getters.childrenByName[newName]
    },
    async cloneFrom ({ dispatch, commit, state, getters }, { path }) {
      if (rootStore.getters.localeMode && !getters.path.startsWith('/assets')) {
        throw new Error('Can\'t clone child. Locale mode active.')
      }
      const newName = randomId(state._children)
      const child = cloneDeep(getters.modulesByPath[path].state)
      commit('addChild', { name: newName, child })
      await dispatch('registerModule', { name: newName })
      return getters.childrenByName[newName]
    },
    async importChild ({ dispatch, commit, state }, { child }) {
      const newName = randomId(state._children)
      const newChild = cloneDeep(child)
      commit('addChild', { name: newName, child: newChild })
      await dispatch('registerModule', { name: newName })
      return newName
    },
    async moveFrom ({ dispatch, getters }, { path }) {
      if (getters.path.startsWith(path)) {
        throw new Error('Can\'t move into self.')
      }
      const newChild = await dispatch('cloneFrom', { path })
      await dispatch([path.substr(1), 'remove'].join('/'), {}, { root: true })
      return newChild
    }
  }

  const getters = {
    modulesByPath: (state, getters, rootState) => rootState._moduleLookup,
    path: (state, getters) => ['', ...getters._path].join('/') || '/', // set path to '/' for root store
    // _path: path as array, set in createModule()
    name: (state, getters) => getters._path.slice(-1)[0],
    parent: (state, getters) => getters.modulesByPath[['', ...getters._path.slice(0, -1)].join('/') || '/'],
    root: (state, getters) => getters.modulesByPath['/'],
    childrenNames: state => !Array.isArray(state._children) ? [] : state._children,
    childrenByName: (state, getters) => getters.childrenNames.reduce((childrenByName, name) => {
      childrenByName[name] = getters.modulesByPath[['', ...getters._path, name].join('/')]
      return childrenByName
    }, {}),
    children: (state, getters) => getters.childrenNames.map(name => getters.childrenByName[name]).filter(child => child !== undefined),
    numChildren: (state, getters) => getters.children.length,
    childrenBySlot: (state, getters) => getters.children.reduce((childrenBySlot, child) => {
      const slot = child.getters.slot
      if (!childrenBySlot[slot]) childrenBySlot[slot] = []
      childrenBySlot[slot].push(child)
      return childrenBySlot
    }, {}),
    numChildrenBySlot: state => Object.keys(getters.childrenBySlot).reduce((numChildrenBySlot, slot) => {
      numChildrenBySlot[slot] = getters.childrenBySlot[slot].length
      return numChildrenBySlot
    }, {}),
    props: state => state._props,
    type: state => componentLookup[state._type],
    slot: state => state._slot || 'default',
    index: (state, getters) => getters.parent.state._children.indexOf(getters.name),
    locales: (state, getters, rootState) => rootState._props._locales || [],
    defaultLocale: (state, getters, rootState) => rootState._props._defaultLocale || ''
  }

  const createModule = (path, type) => {
    const custom = componentLookup[type].customStore || {}
    return {
      state: {
        _props: {},
        _children: []
      },
      getters: {
        ...getters,
        _path: () => path,
        ...(custom.getters || {})
      },
      actions: {
        ...actions,
        ...(custom.actions || {})
      },
      mutations: {
        ...mutations
      },
      namespaced: true
    }
  }

  // TODO: Move root(project) to store module and simplify store create logic
  const store = new Vuex.Store({
    state: {
      _props: {},
      _children: [],
      _moduleLookup: moduleLookup
    },
    /* add some root getters and actions to the regular module stuff */
    getters: {
      ...getters,
      _path: () => [],
      ...((componentLookup.project.customStore || {}).getters || {})
    },
    actions: {
      ...actions,
      replaceState (_, { newState }) {
        // merge in new state to stay reactive
        deepMerge(store.state, newState)
        // remove leftovers
        cleanTree(store.state)
        Vue.set(store.state, '_moduleLookup', moduleLookup)
        return store.dispatch('manageModules', {})
      },
      async manageModules () {
        // unregister old modules, that are not available in state tree anymore
        for (const module in store._modulesNamespaceMap) {
          if (!store._modulesNamespaceMap[module].state._children && !store._modulesNamespaceMap[module].state._props) {
            await store.dispatch(module + 'unregisterModule')
          }
        }
        // register new modules
        await store.dispatch('registerModule', {})
      },
      ...((componentLookup.project.customStore || {}).actions || {})
    },
    mutations: {
      ...mutations,
      setVersion (state, { version }) {
        Vue.set(state, '_version', version)
      }
    },
    plugins
  })

  store.dispatch('registerModule', {})

  return store
}
